import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authFetch, fetchCsrfToken } from './authFetch';
import { Search, Plus, ArrowLeft, Users, TrendingUp, Activity, Shield, ChevronUp, ChevronDown } from 'lucide-react';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CustomerList() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // New customer form state
  const [newCustomer, setNewCustomer] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street_address: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    county: ''
  });
  
  // Pagination and search state
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    totalPages: 0,
    currentPage: 1
  });
  
  // Get current values from URL params
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const searchTerm = searchParams.get('search') || '';
  const sortOrder = searchParams.get('ordering') || '-created_at'; // Default to newest first
  const filter = searchParams.get('filter') || ''; // NEW: Handle filter parameter
  
  const navigate = useNavigate();

  // Update URL params when values change
  const updateUrlParams = useCallback((newParams) => {
    const updatedParams = new URLSearchParams(searchParams);
    
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    
    setSearchParams(updatedParams);
  }, [searchParams, setSearchParams]);

  // Get segment display info
  const getSegmentInfo = (filterKey) => {
    const segments = {
      'all': { name: 'All Customers', icon: Users },
      'new_month': { name: 'New This Month', icon: TrendingUp },
      'active_policies': { name: 'Active Policies', icon: Shield },
      'recent_activity': { name: 'Recent Activity', icon: Activity }
    };
    return segments[filterKey] || { name: 'All Customers', icon: Users };
  };

  const fetchCustomers = useCallback(async (page = 1, search = '', ordering = '-created_at', filterKey = '') => {
    try {
      setLoading(true);
      setError(null);
      
      let url;
      const params = new URLSearchParams({ page: page.toString() });
      
      // NEW: If we have a filter, use the segments endpoint, otherwise use regular customers endpoint
      if (filterKey) {
        url = `${BASE_URL}/api/customers/segments/`;
        params.append('segment', filterKey);
        
        // IMPORTANT: Also pass ordering to segments endpoint
        if (ordering) {
          params.append('ordering', ordering);
        }
      } else {
        url = `${BASE_URL}/api/customers/`;
        
        if (search.trim()) {
          params.append('search', search.trim());
        }
        
        if (ordering) {
          params.append('ordering', ordering);
        }
      }
      
      const fullUrl = `${url}?${params.toString()}`;
      console.log('Fetching customers from:', fullUrl);
      
      const response = await authFetch(fullUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch customers: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Customers response:', data);
      
      // Handle paginated response
      if (data.results) {
        setCustomers(data.results);
        setPagination({
          count: data.count,
          next: data.next,
          previous: data.previous,
          totalPages: Math.ceil(data.count / 50), // Segments use page size of 50
          currentPage: page
        });
      } else {
        // Fallback for non-paginated response
        setCustomers(Array.isArray(data) ? data : []);
        setPagination({
          count: data.length,
          next: null,
          previous: null,
          totalPages: 1,
          currentPage: 1
        });
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setError(error.message);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch customers when URL params change
  useEffect(() => {
    fetchCustomers(currentPage, searchTerm, sortOrder, filter);
  }, [currentPage, searchTerm, sortOrder, filter, fetchCustomers]);

  // Handle search input change with debouncing
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      updateUrlParams({
        search: value,
        page: 1, // Reset to first page when searching
        filter: '' // Clear filter when searching manually
      });
    }, 300); // 300ms delay
    
    setSearchTimeout(timeout);
  };

  // Handle sort order change
  const handleSortChange = (newSortOrder) => {
    updateUrlParams({
      ordering: newSortOrder,
      page: 1 // Reset to first page when changing sort
    });
  };

  // Handle column header click for sorting
  const handleColumnSort = (column) => {
    let newSortOrder;
    
    // Determine new sort direction based on current sort
    const isCurrentColumn = sortOrder.includes(column) || sortOrder.includes(`-${column}`);
    const isDescending = sortOrder.startsWith('-') && sortOrder.slice(1) === column;
    
    if (isCurrentColumn && !isDescending) {
      // Currently ascending, switch to descending
      newSortOrder = `-${column}`;
    } else {
      // Currently descending or different column, switch to ascending
      newSortOrder = column;
    }
    
    console.log(`Column sort clicked: ${column}, current sort: ${sortOrder}, new sort: ${newSortOrder}`);
    handleSortChange(newSortOrder);
  };

  // Get sort indicator for column
  const getSortIndicator = (column) => {
    // More precise checking for current column
    const isDescending = sortOrder === `-${column}`;
    const isAscending = sortOrder === column;
    
    if (isDescending) {
      return <ChevronDown className="h-4 w-4 text-blue-600" />;
    } else if (isAscending) {
      return <ChevronUp className="h-4 w-4 text-blue-600" />;
    }
    
    return null;
  };

  // Sortable Column Header Component
  const SortableColumnHeader = ({ column, children, className = "" }) => {
    const isCurrentColumn = sortOrder === column || sortOrder === `-${column}`;
    
    return (
      <th 
        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors ${
          isCurrentColumn ? 'bg-blue-50 text-blue-600' : ''
        } ${className}`}
        onClick={() => handleColumnSort(column)}
      >
        <div className="flex items-center space-x-1">
          <span>{children}</span>
          <div className="flex flex-col">
            {getSortIndicator(column)}
          </div>
        </div>
      </th>
    );
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      updateUrlParams({ page });
    }
  };

  // Handle adding new customer
  const handleAddCustomer = async () => {
    const { first_name, last_name } = newCustomer;
    if (!first_name || !last_name) {
      alert('First and last name are required.');
      return;
    }

    try {
      // CRITICAL FIX: Ensure we have a fresh CSRF token before POST request
      await fetchCsrfToken();
      
      const response = await authFetch(`${BASE_URL}/api/customers/`, {
        method: 'POST',
        body: JSON.stringify(newCustomer),
      });

      if (!response.ok) {
        throw new Error('Failed to create customer');
      }

      const createdCustomer = await response.json();
      setNewCustomer({ 
        first_name: '', 
        last_name: '', 
        email: '', 
        phone: '', 
        street_address: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: '',
        county: ''
      });
      setShowForm(false);
      
      // Refresh the current page to show the new customer
      fetchCustomers(currentPage, searchTerm, sortOrder, filter);
      
      // Navigate to the new customer's profile
      navigate(`/customers/${createdCustomer.id}`);
    } catch (error) {
      console.error("Error creating customer:", error);
      setError(error.message);
    }
  };

  // Pagination component
  const PaginationControls = () => {
    const startItem = (pagination.currentPage - 1) * 50 + 1;
    const endItem = Math.min(pagination.currentPage * 50, pagination.count);
    
    // Calculate page range to show
    const maxPageButtons = 5;
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxPageButtons - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }
    
    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (pagination.totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-6 px-4 py-3 bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center text-sm text-gray-700">
          <span>
            Showing {startItem} to {endItem} of {pagination.count} customers
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Previous button */}
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.previous}
            className={`px-3 py-1 rounded text-sm ${
              pagination.previous 
                ? 'text-blue-600 hover:bg-blue-50 border border-blue-300' 
                : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
            }`}
          >
            Previous
          </button>
          
          {/* Page numbers */}
          {pageNumbers.map(pageNum => (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={`px-3 py-1 rounded text-sm ${
                pageNum === pagination.currentPage
                  ? 'bg-blue-600 text-white'
                  : 'text-blue-600 hover:bg-blue-50 border border-blue-300'
              }`}
            >
              {pageNum}
            </button>
          ))}
          
          {/* Next button */}
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.next}
            className={`px-3 py-1 rounded text-sm ${
              pagination.next 
                ? 'text-blue-600 hover:bg-blue-50 border border-blue-300' 
                : 'text-gray-400 bg-gray-100 border border-gray-200 cursor-not-allowed'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Get segment info for display
  const segmentInfo = getSegmentInfo(filter);
  const SegmentIcon = segmentInfo.icon;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-lg text-gray-600">Loading customers...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="text-lg text-red-600 mb-2">Error Loading Customers</div>
          <div className="text-gray-600">{error}</div>
          <button 
            onClick={() => fetchCustomers(currentPage, searchTerm, sortOrder, filter)}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center">
              <SegmentIcon className="h-6 w-6 text-blue-500 mr-2" />
              <h1 className="text-2xl font-bold text-gray-900">{segmentInfo.name}</h1>
            </div>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            {showForm ? 'Cancel' : 'Add Customer'}
          </button>
        </div>
      </div>

      {/* Search and Filters - Only show when not using a specific filter */}
      {!filter && (
        <div className="mb-6 bg-white p-4 border border-gray-200 rounded-lg">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search customers..."
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={sortOrder}
              onChange={(e) => handleSortChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="first_name">First Name A-Z</option>
              <option value="-first_name">First Name Z-A</option>
              <option value="last_name">Last Name A-Z</option>
              <option value="-last_name">Last Name Z-A</option>
              <option value="email">Email A-Z</option>
              <option value="-email">Email Z-A</option>
              <option value="phone">Phone Number</option>
              <option value="-phone">Phone Number (Reverse)</option>
              <option value="city">City A-Z</option>
              <option value="-city">City Z-A</option>
              <option value="-created_at">Newest First</option>
              <option value="created_at">Oldest First</option>
            </select>
          </div>
        </div>
      )}

      {/* Results Summary */}
      {!loading && (
        <div className="mb-4 text-sm text-gray-600">
          {searchInput && !filter ? (
            <span>Found {pagination.count} customers matching "{searchInput}"</span>
          ) : (
            <span>Showing {pagination.count} customers</span>
          )}
        </div>
      )}

      {/* Add Customer Form */}
      {showForm && (
        <div className="mb-6 bg-white p-6 border border-gray-200 rounded-lg shadow-sm">
          <h2 className="text-lg font-semibold mb-4">Add New Customer</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="First Name *"
              value={newCustomer.first_name}
              onChange={(e) => setNewCustomer({ ...newCustomer, first_name: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="text"
              placeholder="Last Name *"
              value={newCustomer.last_name}
              onChange={(e) => setNewCustomer({ ...newCustomer, last_name: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="email"
              placeholder="Email"
              value={newCustomer.email}
              onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div>
              <input
                type="tel"
                placeholder="Phone (e.g., 352-450-1155)"
                value={newCustomer.phone}
                onChange={(e) => {
                  // Auto-format phone number with dashes
                  let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                  if (value.length >= 6) {
                    value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                  } else if (value.length >= 3) {
                    value = value.slice(0, 3) + '-' + value.slice(3);
                  }
                  setNewCustomer({ ...newCustomer, phone: value });
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">Format: 352-450-1155</p>
            </div>
            
            <input
              type="text"
              placeholder="Street Address"
              value={newCustomer.street_address}
              onChange={(e) => setNewCustomer({ ...newCustomer, street_address: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:col-span-2"
            />
            <input
              type="text"
              placeholder="Address Line 2 (Optional)"
              value={newCustomer.address_line2}
              onChange={(e) => setNewCustomer({ ...newCustomer, address_line2: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:col-span-2"
            />
            <input
              type="text"
              placeholder="City"
              value={newCustomer.city}
              onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              type="text"
              placeholder="State (e.g., FL)"
              value={newCustomer.state}
              onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value.toUpperCase() })}
              maxLength="2"
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div>
              <input
                type="text"
                placeholder="Zip Code (5 digits)"
                value={newCustomer.zip_code}
                onChange={(e) => {
                  // Only allow digits and limit to 5 characters
                  const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setNewCustomer({ ...newCustomer, zip_code: value });
                }}
                maxLength="5"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">5 digits only (e.g., 32162)</p>
            </div>
            <input
              type="text"
              placeholder="County"
              value={newCustomer.county}
              onChange={(e) => setNewCustomer({ ...newCustomer, county: e.target.value })}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleAddCustomer}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
            >
              Add Customer
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Customer List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {customers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No customers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableColumnHeader column="first_name">
                    Customer
                  </SortableColumnHeader>
                  <SortableColumnHeader column="email">
                    Contact
                  </SortableColumnHeader>
                  <SortableColumnHeader column="city">
                    Location
                  </SortableColumnHeader>
                  <SortableColumnHeader column="created_at">
                    Added
                  </SortableColumnHeader>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map((customer) => (
                  <tr 
                    key={customer.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {customer.first_name && customer.last_name 
                            ? `${customer.first_name} ${customer.last_name}` 
                            : `Customer #${customer.id}`
                          }
                        </div>
                        <div className="text-sm text-gray-500">ID: {customer.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{customer.email || 'No email'}</div>
                      <div className="text-sm text-gray-500">{customer.phone || 'No phone'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {[customer.city, customer.state].filter(Boolean).join(', ') || 'No location'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.created_at ? new Date(customer.created_at).toLocaleDateString() : 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/customers/${customer.id}`);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <PaginationControls />
    </div>
  );
}

export default CustomerList;